import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowRight,
  Search as SearchIcon,
  MapPin,
  ShieldCheck,
  CreditCard,
  Headphones,
  Star,
  Smartphone,
  Sparkles,
  Plane,
  Navigation,
} from 'lucide-react';
import { BRAND } from '../brand';
import StickyMobileCta from '../components/StickyMobileCta';
import WorldCupCountdown from '../components/WorldCupCountdown';
import LocationAutocomplete from '../components/LocationAutocomplete';

const Home = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const submitSearch = (e) => {
    e?.preventDefault?.();
    if (query.trim()) {
      navigate(`/search?location=${encodeURIComponent(query.trim())}`);
    } else {
      navigate('/search');
    }
  };

  return (
    <div className="bg-white text-ink-900 -mt-16 sm:-mt-20 font-mont">
      {/* ===========================================================
          HERO — cinematic, dark overlay, transparent nav above
      ============================================================ */}
      <section
        className="relative min-h-[100svh] flex items-end sm:items-center pt-28 sm:pt-32 pb-32 sm:pb-24 overflow-hidden grain-overlay"
        data-testid="home-hero"
      >
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={BRAND.landing.hero}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-3xl">
            <span
              className="mob-fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lime/15 backdrop-blur ring-1 ring-lime/40 text-lime text-xs font-comfortaa font-bold tracking-[0.18em] uppercase"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse" />
              FIFA World Cup 2026 Ready
            </span>

            <h1
              className="mob-fade-up mob-delay-1 mt-5 font-comfortaa font-bold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight"
              data-testid="hero-title"
            >
              {t('heroTitle')}
            </h1>

            <p className="mob-fade-up mob-delay-2 mt-5 text-base sm:text-lg lg:text-xl text-white/80 font-light max-w-2xl leading-relaxed">
              {t('heroSubtitle')}
            </p>

            {/* Premium search with Places autocomplete */}
            <form
              onSubmit={submitSearch}
              className="mob-fade-up mob-delay-3 mt-8"
              data-testid="hero-search-form"
            >
              <div className="flex items-center bg-white rounded-full p-1.5 shadow-2xl ring-1 ring-black/5 max-w-2xl">
                <LocationAutocomplete
                  value={query}
                  onChange={setQuery}
                  onPlaceSelected={(p) => setQuery(p?.formatted_address || p?.name || '')}
                  placeholder={t('searchPlaceholder')}
                  wrapperClassName="flex-1 min-w-0"
                  className="border-0 shadow-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-12 text-sm sm:text-base font-light placeholder:text-ink-400 w-full"
                  testId="hero-search-input"
                />
                <Button
                  type="submit"
                  className="rounded-full h-11 sm:h-12 px-5 sm:px-7 bg-ink hover:bg-ink-700 text-white font-comfortaa font-bold text-sm shrink-0"
                  data-testid="hero-search-submit"
                >
                  <SearchIcon className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('searchButton')}</span>
                </Button>
              </div>
            </form>

            {/* Sede chips */}
            <div className="mob-fade-up mob-delay-3 mt-4 flex flex-wrap items-center gap-2" data-testid="hero-sede-chips">
              <span className="text-[10px] font-comfortaa font-bold tracking-[0.22em] uppercase text-white/55 mr-1">Sedes Mundial</span>
              {[
                { id: 'cdmx', label: 'CDMX', q: 'Ciudad de México' },
                { id: 'mty', label: 'Monterrey', q: 'Monterrey' },
                { id: 'gdl', label: 'Guadalajara', q: 'Guadalajara' },
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => navigate(`/search?location=${encodeURIComponent(s.q)}`)}
                  data-testid={`hero-chip-${s.id}`}
                  className="px-3.5 py-1.5 rounded-full border border-white/25 bg-white/10 backdrop-blur text-white text-xs font-comfortaa font-bold hover:bg-lime hover:text-ink hover:border-lime transition"
                >
                  {s.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  if (!navigator.geolocation) return;
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      const q = `${pos.coords.latitude},${pos.coords.longitude}`;
                      navigate(`/search?near=${q}`);
                    },
                    () => navigate('/search')
                  );
                }}
                data-testid="hero-chip-near"
                className="px-3.5 py-1.5 rounded-full border border-lime/40 bg-lime/15 text-lime text-xs font-comfortaa font-bold hover:bg-lime hover:text-ink hover:border-lime transition inline-flex items-center gap-1.5"
              >
                <Navigation className="w-3 h-3" /> Cerca de mí
              </button>
            </div>

            {/* CTAs */}
            <div className="mob-fade-up mob-delay-4 mt-6 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => navigate('/search')}
                className="rounded-full h-12 px-6 bg-lime hover:bg-lime-500 text-ink font-comfortaa font-bold text-sm shadow-[0_8px_24px_-8px_rgba(188,239,60,0.6)]"
                data-testid="hero-cta-reserve"
              >
                {t('heroCtaPrimary')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/become-mobilitier')}
                className="rounded-full h-12 px-6 bg-white/10 hover:bg-white/15 text-white border border-white/20 backdrop-blur font-comfortaa font-bold text-sm"
                data-testid="hero-cta-publish"
              >
                {t('heroCtaSecondary')}
              </Button>
            </div>

            <p className="mob-fade-up mob-delay-4 mt-8 text-xs sm:text-sm text-white/55 font-light max-w-xl">
              {t('heroMicrocopy')}
            </p>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-[1px] h-12 bg-gradient-to-b from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* WORLD CUP COUNTDOWN BAR */}
      <WorldCupCountdown />

      {/* ===========================================================
          WORLD CUP 2026 BANNER SECTION
      ============================================================ */}
      <section
        id="mundial-2026"
        className="relative bg-black text-white py-20 sm:py-28 overflow-hidden"
        data-testid="home-worldcup"
      >
        <div className="absolute inset-0 opacity-30">
          <img
            src={BRAND.landing.worldCupBanner}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-lime text-xs font-comfortaa font-bold tracking-[0.25em]">
              {t('wcEyebrow')}
            </span>
            <h2 className="mt-4 font-comfortaa font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {t('wcTitle')}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/70 font-light max-w-xl leading-relaxed">
              {t('wcParagraph')}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/search')}
                className="rounded-full h-12 px-6 bg-lime hover:bg-lime-500 text-ink font-comfortaa font-bold text-sm"
                data-testid="wc-cta-reserve"
              >
                {t('wcCtaReserve')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/become-mobilitier')}
                className="rounded-full h-12 px-6 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white font-comfortaa font-bold text-sm"
                data-testid="wc-cta-earn"
              >
                {t('wcCtaEarn')}
              </Button>
            </div>
          </div>

          {/* Stat grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {[
              { n: '3', label: t('wcStat1') },
              { n: '∞', label: t('wcStat2') },
              { n: '< 2 min', label: t('wcStat3') },
              { n: '2026', label: t('wcStat4') },
            ].map((s, i) => (
              <div
                key={i}
                className="relative p-5 sm:p-7 rounded-2xl bg-white/[0.04] backdrop-blur ring-1 ring-white/10 hover:ring-lime/40 transition"
              >
                <div className="font-comfortaa font-bold text-3xl sm:text-4xl text-lime">{s.n}</div>
                <div className="mt-2 text-xs sm:text-sm text-white/70 font-light">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===========================================================
          HOST CITIES
      ============================================================ */}
      <section className="bg-white py-20 sm:py-28" data-testid="home-cities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-comfortaa font-bold text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight">
              {t('citiesTitle')}
            </h2>
            <p className="mt-4 text-base text-ink-500 font-light">{t('citiesSubtitle')}</p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {BRAND.cities.map((city, idx) => (
              <button
                key={city.id}
                onClick={() =>
                  navigate(`/search?location=${encodeURIComponent(city.name)}`)
                }
                data-testid={`city-card-${city.id}`}
                className={`group relative overflow-hidden rounded-3xl text-left transition transform hover:-translate-y-1 ${
                  idx === 0 ? 'md:row-span-2 md:aspect-[3/4]' : 'aspect-[4/3]'
                }`}
              >
                <img
                  src={city.image}
                  alt={city.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7">
                  <div className="flex items-center gap-2 text-lime text-[11px] font-comfortaa font-bold tracking-[0.2em]">
                    <span className="w-6 h-px bg-lime" /> {city.slug}
                  </div>
                  <h3 className="mt-2 text-white font-comfortaa font-bold text-2xl sm:text-3xl">
                    {city.name}
                  </h3>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm text-white/85 font-light">
                    Reservar autos disponibles
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===========================================================
          APP / MOCKUP SECTION
      ============================================================ */}
      <section className="bg-ink-50 py-20 sm:py-28" data-testid="home-app">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-ink-500 text-xs font-comfortaa font-bold tracking-[0.25em]">
              MOBILE FIRST
            </span>
            <h2 className="mt-4 font-comfortaa font-bold text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight">
              {t('appTitle')}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-ink-500 font-light max-w-xl leading-relaxed">
              {t('appSubtitle')}
            </p>
            <ul className="mt-8 space-y-3">
              {[
                'Reserva en menos de 2 minutos',
                'Check-in y check-out con fotos',
                'Pagos seguros con Stripe',
                'Chat directo con tu Mobilitier',
              ].map((line) => (
                <li key={line} className="flex items-center gap-3 text-sm text-ink-700 font-light">
                  <span className="w-5 h-5 rounded-full bg-lime grid place-items-center shrink-0">
                    <Sparkles className="w-3 h-3 text-ink" />
                  </span>
                  {line}
                </li>
              ))}
            </ul>
          </div>

          {/* Phone mockup (CSS-only premium) */}
          <div className="relative mx-auto w-full max-w-sm aspect-[9/19] rounded-[40px] bg-ink p-2 shadow-2xl ring-1 ring-black/10">
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-full z-10" />
            <div className="relative w-full h-full rounded-[34px] overflow-hidden bg-white">
              <img
                src={BRAND.landing.travelerLifestyle}
                alt="Mobility app"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                <div className="text-[11px] font-comfortaa font-bold tracking-widest text-lime">
                  RESERVA ACTIVA
                </div>
                <div className="mt-1 font-comfortaa font-bold text-xl">CDMX → Estadio Azteca</div>
                <div className="mt-1 text-xs font-light text-white/75">11 jun · Sedán · Mobility verificado</div>
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-lime text-ink text-xs font-comfortaa font-bold">
                  <Smartphone className="w-3.5 h-3.5" />
                  Abrir en la app
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===========================================================
          TRUST SECTION
      ============================================================ */}
      <section className="bg-white py-20 sm:py-28" data-testid="home-trust">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-comfortaa font-bold text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight">
              {t('trustTitle')}
            </h2>
            <p className="mt-4 text-base text-ink-500 font-light">{t('trustSubtitle')}</p>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { Icon: ShieldCheck, label: t('trustVerified') },
              { Icon: CreditCard, label: t('trustSecure') },
              { Icon: Headphones, label: t('trustSupport') },
              { Icon: Star, label: t('trustReviews') },
            ].map(({ Icon, label }) => (
              <div
                key={label}
                className="group p-6 sm:p-7 rounded-2xl bg-ink-50 hover:bg-ink hover:text-white transition border border-ink-100"
              >
                <div className="w-11 h-11 rounded-xl bg-lime grid place-items-center group-hover:scale-105 transition">
                  <Icon className="w-5 h-5 text-ink" />
                </div>
                <div className="mt-5 font-comfortaa font-bold text-base sm:text-lg">{label}</div>
                <div className="mt-2 text-xs text-ink-500 group-hover:text-white/60 font-light">
                  Estándar Mobility
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===========================================================
          TRAVELER LIFESTYLE
      ============================================================ */}
      <section
        className="relative py-24 sm:py-32 overflow-hidden bg-black text-white"
        data-testid="home-traveler"
      >
        <div className="absolute inset-0">
          <img
            src={BRAND.landing.travelerAirport}
            alt=""
            className="w-full h-full object-cover opacity-50"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-lime text-xs font-comfortaa font-bold tracking-[0.25em]">
              <Plane className="w-3.5 h-3.5" /> EXPLORA MÉXICO
            </span>
            <h2 className="mt-4 font-comfortaa font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {t('travelerTitle')}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/70 font-light max-w-xl leading-relaxed">
              {t('travelerSubtitle')}
            </p>
            <div className="mt-8">
              <Button
                onClick={() => navigate('/search')}
                className="rounded-full h-12 px-7 bg-lime hover:bg-lime-500 text-ink font-comfortaa font-bold text-sm"
                data-testid="traveler-cta"
              >
                Encontrar mi auto
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom safe-area for sticky mobile CTA */}
      <div className="h-20 md:h-0" aria-hidden />

      <StickyMobileCta />
    </div>
  );
};

export default Home;
